import { useQuery } from '@tanstack/react-query';
import { eventsApi } from '@/features/events/api';
import { queryKeys } from '@/lib/api/queryKeys';
import { xurveyApi } from '@/lib/api/xurvey';
import type { XkStation } from '@/lib/api/types';

/** Un despliegue del alcance, con la encuesta que corre y la estación que lo sirve. */
export interface ScopeLink {
  id: number;
  surveyId: number;
  stationId: number;
  eventId: number;
}

/**
 * Los despliegues de Xurvey que caen dentro de unos eventos.
 *
 * Es la misma cuenta que hace el Design Journey para saber en qué enlaces
 * escribe, y aquí sirve para saber de qué enlaces LEER: el informe de un
 * evento suma sus estaciones y el de un proyecto las de todos sus eventos.
 * Toda respuesta se ata a un `publicLinkId`, así que éste es el único puente
 * entre «este evento» y «estas respuestas».
 *
 * Los QR de PRUEBA quedan fuera: no guardan nada, y contarlos sería contar
 * ensayos. Los enlaces cerrados de una configuración anterior se quedan: sus
 * respuestas fueron reales.
 */
export function useScopeDeployments(eventIds: readonly number[]) {
  const ids = [...eventIds].filter((n) => Number.isFinite(n) && n > 0);
  return useQuery({
    queryKey: queryKeys.xurvey.scopeDeployments(ids),
    enabled: ids.length > 0,
    staleTime: 60_000,
    queryFn: async (): Promise<ScopeLink[]> => {
      /*
       * Tolerante por partes: una estación que apunte a una encuesta borrada
       * (pasa en dev: la 2 ya no existe) devolvía 404 y se llevaba el alcance
       * ENTERO — el informe del proyecto decía «sin encuesta desplegada» con
       * doce respuestas debajo. Lo que falle se salta; el resto cuenta.
       */
      const listas = await Promise.allSettled(ids.map((id) => eventsApi.listStations({ eventId: id })));
      const estaciones = listas.flatMap((l, i) =>
        l.status === 'fulfilled'
          ? ((l.value.items ?? []) as XkStation[]).map((s) => ({ ...s, eventId: s.eventId ?? ids[i]! }))
          : [],
      );
      const surveyIds = [...new Set(estaciones.map((s) => s.surveysId).filter((x): x is number => !!x))];
      const porEncuesta = await Promise.allSettled(surveyIds.map((sid) => xurveyApi.listDeployments(sid)));
      const porEstacion = new Map(estaciones.map((s) => [String(s.id), s]));
      const out: ScopeLink[] = [];
      porEncuesta.forEach((resultado, i) => {
        if (resultado.status !== 'fulfilled') return;
        for (const d of resultado.value) {
          if (d.isTest) continue;
          const estacion = porEstacion.get(String(d.entityId));
          if (!estacion) continue;
          out.push({ id: d.id, surveyId: surveyIds[i]!, stationId: estacion.id, eventId: estacion.eventId });
        }
      });
      return out;
    },
  });
}
